/**
 * ChatInput - Enhanced input area with file upload and voice toggle.
 *
 * Features:
 * - Text input with auto-resize
 * - File attachment button
 * - Voice toggle FAB
 * - Send button with loading state
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Loader2, Paperclip, Mic } from 'lucide-react'
import { FileDropZone } from './FileDropZone'
import { FilePreviewList } from './FilePreviewList'
import type { FileAttachment } from './FilePreviewChip'

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (attachments?: FileAttachment[]) => void
  onVoiceClick?: () => void
  isLoading: boolean
  disabled?: boolean
  placeholder?: string
  showVoiceButton?: boolean
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  onVoiceClick,
  isLoading,
  disabled = false,
  placeholder = 'Ask me anything about your home...',
  showVoiceButton = true,
}: ChatInputProps) {
  const [attachments, setAttachments] = useState<FileAttachment[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleFilesSelected = useCallback((files: File[]) => {
    const newAttachments: FileAttachment[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      preview: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined,
      uploading: false,
      uploadProgress: 0,
    }))

    setAttachments((prev) => [...prev, ...newAttachments].slice(0, 5))
  }, [])

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id)
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview)
      }
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if ((!value.trim() && attachments.length === 0) || isLoading) return

      onSubmit(attachments.length > 0 ? attachments : undefined)
      setAttachments([])
    },
    [value, attachments, isLoading, onSubmit]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit(e)
      }
    },
    [handleSubmit]
  )

  const canSubmit = (value.trim() || attachments.length > 0) && !isLoading

  return (
    <FileDropZone
      onFilesSelected={handleFilesSelected}
      disabled={disabled}
      className="border-t border-surface-border bg-background/80 backdrop-blur"
    >
      {/* File previews */}
      <FilePreviewList attachments={attachments} onRemove={handleRemoveAttachment} />

      {/* Input area - safe area padding for mobile devices with notches */}
      <div className="p-2 sm:p-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          {/* File attachment button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isLoading || attachments.length >= 5}
            className="btn-ghost p-2 flex-shrink-0"
            aria-label="Attach files"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.md,.csv"
            onChange={(e) => {
              if (e.target.files) {
                handleFilesSelected(Array.from(e.target.files))
              }
              e.target.value = ''
            }}
            className="hidden"
          />

          {/* Text input */}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="input flex-1 text-base min-w-0 bg-surface"
            disabled={disabled || isLoading}
            autoFocus
          />

          {/* Voice button */}
          {showVoiceButton && onVoiceClick && (
            <button
              type="button"
              onClick={onVoiceClick}
              disabled={disabled}
              className="btn-ghost p-2 flex-shrink-0 hover:text-accent"
              aria-label="Start voice mode"
            >
              <Mic className="w-5 h-5" />
            </button>
          )}

          {/* Send button */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn-primary flex-shrink-0 px-4"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </FileDropZone>
  )
}

// Re-export FileAttachment type
export type { FileAttachment }

export default ChatInput
